import React, { useState } from 'react';
import { TextField, FormControl, RadioGroup, FormControlLabel, Radio, Typography, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import CountrySelect from '../../components/CountriesDropBox/CountriesDropBox';
import {
    BackgroundContainer,
    LoginContainer,
    PaperContainer,
    LogoContainer,
    FormContainer,
    SubmitButton,
    BackButton,
    ReturningCustomerLink,
    InformationHeading,
} from './ProceedToPreorderPage.styles';
import { signup, signin } from '../../api/authApi';
import {useToast} from '../../components/ToastProvider/ToastProvider';

function ProceedToPreorderPage() {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [errors, setErrors] = useState({});

    const [billing, setBilling] = useState({
        street: '',
        suburb: '',
        city: '',
        state: '',
        postal: '',
        country: '',
    });
    const [shipping, setShipping] = useState({
        street: '',
        suburb: '',
        city: '',
        state: '',
        postal: '',
        country: '',
    });
    const [accountType, setAccountType] = useState('individual');
    const [businessInfo, setBusinessInfo] = useState({
        legalName: '',
        registrationNumber: '',
        vatNumber: '',
        eoriNumber: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const { showToast } = useToast();

    const handlePasswordChange = (setter) => (e) => {
        const newPassword = e.target.value;
        setter(newPassword);
        if (setter === setPassword) {
            if (newPassword.length > 0 && newPassword.length < 8) {
                setPasswordError('Password must be at least 8 characters long');
            } else {
                setPasswordError('');
            }
        }
        setErrors((prev) => ({ ...prev, password: false }));
    };

    const isPasswordValid = password.length >= 8;

    // Mobile number validation
    const validateMobileNumber = (mobileNumber) => {
        const cleanMobile = mobileNumber.replace(/\D/g, ''); // Remove non-digits
        return cleanMobile.length >= 4 && cleanMobile.length <= 20;
    };

    const handleMobileChange = (e) => {
        const mobileValue = e.target.value;
        setMobile(mobileValue);
        
        if (mobileValue && !validateMobileNumber(mobileValue)) {
            setMobileError('Mobile number must be at least 4 digits');
        } else {
            setMobileError('');
        }
        
        setErrors((prev) => ({ ...prev, mobile: false }));
    };

    const handleSameAsAbove = () => {
        const allFilled = Object.values(billing).every(Boolean);
        if (allFilled) {
            setShipping({ ...billing });
            showToast('Shipping address copied from billing', 'success');
            clearError('shipping');
        } else {
            showToast('Please fill in all billing fields first', 'warning');
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Basic info validation (same as RegisterPage Step 1)
        if (!fullName.trim()) newErrors.fullName = true;
        if (!email.trim()) newErrors.email = true;
        if (!mobile.trim()) newErrors.mobile = true;
        if (!validateMobileNumber(mobile)) newErrors.mobile = true;
        if (!isPasswordValid) newErrors.password = true;
        if (!confirmPassword.trim()) newErrors.confirmPassword = true;
        if (password !== confirmPassword) newErrors.confirmPassword = true;

        // Address validation (same as RegisterPage Step 2)
        newErrors.billing = {};
        newErrors.shipping = {};
        const requiredBilling = ['street', 'city', 'state', 'postal', 'country'];
        const requiredShipping = ['street', 'city', 'state', 'postal', 'country'];

        for (const field of requiredBilling) {
            const value = billing[field];
            const isValid = value && value.trim();
            if (!isValid) {
                newErrors.billing[field] = true;
            }
        }
        for (const field of requiredShipping) {
            const value = shipping[field];
            const isValid = value && value.trim();
            if (!isValid) {
                newErrors.shipping[field] = true;
            }
        }

        // Business info validation (same as RegisterPage Step 3)
        if (accountType === 'business') {
            newErrors.businessInfo = {};
            if (!businessInfo.legalName.trim()) newErrors.businessInfo.legalName = true;
        }

        setErrors(newErrors);

        // Use same validation logic as RegisterPage
        const basicFieldErrors = Object.keys(newErrors).filter(key => 
            !['billing', 'shipping', 'businessInfo'].includes(key)
        );
        const hasErrors = basicFieldErrors.length > 0 || 
                         Object.keys(newErrors.billing || {}).length > 0 ||
                         Object.keys(newErrors.shipping || {}).length > 0 ||
                         (accountType === 'business' && Object.keys(newErrors.businessInfo || {}).length > 0);


        if (hasErrors) {
            showToast('Please fill in all required fields correctly', 'error');
            return false;
        }
        return true;
    };

    const handleProceed = async () => {
        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const shippingSameAsBilling =
                shipping.street === billing.street &&
                shipping.suburb === billing.suburb &&
                shipping.city === billing.city &&
                shipping.state === billing.state &&
                shipping.postal === billing.postal &&
                shipping.country === billing.country;

            const signupData = {
                customer_info: {
                    full_name: fullName.trim(),
                    email: email.trim(),
                    mobile_no: mobile.replace(/\D/g, ''),
                    password,
                    confirm_password: confirmPassword,
                },
                billing_shipping: {
                    shipping_same_as_billing: shippingSameAsBilling,
                    billing_address: {
                        street_address: billing.street,
                        suburb_locality: billing.suburb || null,
                        city_town: billing.city,
                        state_province_region: billing.state,
                        postal_zip_code: billing.postal,
                        country: billing.country,
                    },
                    shipping_address: shippingSameAsBilling
                        ? null
                        : {
                              street_address: shipping.street,
                              suburb_locality: shipping.suburb || null,
                              city_town: shipping.city,
                              state_province_region: shipping.state,
                              postal_zip_code: shipping.postal,
                              country: shipping.country,
                          },
                },
                business_info: {
                    is_individual: accountType === 'individual',
                    legal_business_name: accountType === 'individual' ? null : businessInfo.legalName,
                    registration_number:
                        accountType === 'individual'
                            ? null
                            : (businessInfo.registrationNumber?.trim() || null),
                    vat_tax_number: accountType === 'individual' ? null : (businessInfo.vatNumber?.trim() || null),
                    eori_import_export_number: accountType === 'individual' ? null : businessInfo.eoriNumber,
                },
                is_admin: false,
            };

            // Create user in database
            await signup(signupData);

            // Sign in user to get access token
            const authData = await signin(email.trim(), password);

            // Store authentication data for logged-in state
            if (authData.access_token) {
                localStorage.setItem('accessToken', authData.access_token);
            }
            localStorage.setItem('isAdmin', authData.is_admin ? 'true' : 'false');

            // Small delay to ensure localStorage is updated
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Redirect to products page as logged-in user with cart window open
            navigate('/landing/products', { state: { openCart: true } });
        } catch (err) {
            showToast(err.message || 'Registration failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginClick = () => {
        navigate('/login', {
            state: { redirectTo: '/landing/products', openCart: true },
        });
    };

    const clearError = (fieldPath) => {
        setErrors((prev) => {
            const updated = { ...prev };
            const parts = fieldPath.split('.');
            if (parts.length === 1) delete updated[parts[0]];
            else if (parts.length === 2 && updated[parts[0]]) delete updated[parts[0]][parts[1]];
            return updated;
        });
    };

    return (
        <>
            <TopNavbar />
            <BackgroundContainer>
                <LoginContainer>
                    <PaperContainer elevation={3}>
                        <LogoContainer>
                            <img src="/APD_Login.png" alt="Logo" />
                        </LogoContainer>

                        <ReturningCustomerLink>
                            Returning customer?{' '}
                            <Link 
                                component="button" 
                                onClick={handleLoginClick}
                                sx={{ 
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    color: '#002F87',
                                    fontWeight: 'bold'
                                }}
                            >
                                Click here to login
                            </Link>
                        </ReturningCustomerLink>

                        <InformationHeading variant="h4" component="h1">
                            Your Information
                        </InformationHeading>

                        <FormContainer>
                            {/* Basic Information */}
                            <Typography variant="h6" sx={{ mb: 2, color: '#002F87', fontWeight: 'bold' }}>
                                Basic Information
                            </Typography>
                            
                            <TextField
                                required
                                fullWidth
                                label="Full Name"
                                value={fullName}
                                onChange={(e) => {
                                    setFullName(e.target.value);
                                    clearError('fullName');
                                }}
                                error={!!errors.fullName}
                                helperText={errors.fullName ? 'This field is required' : ''}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    clearError('email');
                                }}
                                error={!!errors.email}
                                helperText={errors.email ? 'This field is required' : ''}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Mobile No"
                                type="tel"
                                value={mobile}
                                onChange={handleMobileChange}
                                error={!!mobileError || !!errors.mobile}
                                helperText={mobileError || (errors.mobile ? 'Mobile number is required and must be at least 4 digits' : '')}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Enter Password"
                                type="password"
                                value={password}
                                onChange={handlePasswordChange(setPassword)}
                                error={!!errors.password || !!passwordError}
                                helperText={passwordError || (errors.password ? 'This field is required' : 'Minimum 8 characters required')}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Confirm Password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    clearError('confirmPassword');
                                }}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword ? 'Passwords must match' : ''}
                                sx={{ mb: 3 }}
                            />

                            {/* Billing Address */}
                            <Typography variant="h6" sx={{ mb: 2, color: '#002F87', fontWeight: 'bold' }}>
                                Billing Address
                            </Typography>
                            {['street', 'suburb', 'city', 'state', 'postal'].map((field) => (
                                <TextField
                                    key={field}
                                    required
                                    fullWidth
                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                    value={billing[field]}
                                    onChange={(e) => {
                                        setBilling({ ...billing, [field]: e.target.value });
                                        clearError(`billing.${field}`);
                                    }}
                                    error={!!errors.billing?.[field]}
                                    helperText={errors.billing?.[field] ? 'This field is required' : ''}
                                    sx={{ mb: 2 }}
                                />
                            ))}
                            <CountrySelect
                                value={billing.country}
                                onChange={(value) => {
                                    setBilling({ ...billing, country: value });
                                    clearError('billing.country');
                                }}
                            />
                            {errors.billing?.country && (
                                <Typography color="error" variant="caption" style={{ marginTop: '4px', display: 'block' }}>
                                    This field is required
                                </Typography>
                            )}

                            {/* Shipping Address */}
                            <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#002F87', fontWeight: 'bold' }}>
                                Shipping Address
                            </Typography>
                            <BackButton type="button" onClick={handleSameAsAbove} style={{ marginBottom: '16px' }}>
                                Same as above
                            </BackButton>
                            {['street', 'suburb', 'city', 'state', 'postal'].map((field) => (
                                <TextField
                                    key={field}
                                    required
                                    fullWidth
                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                    value={shipping[field]}
                                    onChange={(e) => {
                                        setShipping({ ...shipping, [field]: e.target.value });
                                        clearError(`shipping.${field}`);
                                    }}
                                    error={!!errors.shipping?.[field]}
                                    helperText={errors.shipping?.[field] ? 'This field is required' : ''}
                                    sx={{ mb: 2 }}
                                />
                            ))}
                            <CountrySelect
                                value={shipping.country}
                                onChange={(value) => {
                                    setShipping({ ...shipping, country: value });
                                    clearError('shipping.country');
                                }}
                            />
                            {errors.shipping?.country && (
                                <Typography color="error" variant="caption" style={{ marginTop: '4px', display: 'block' }}>
                                    This field is required
                                </Typography>
                            )}

                            {/* Account Type */}
                            <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#002F87', fontWeight: 'bold' }}>
                                Account Type
                            </Typography>
                            <FormControl component="fieldset" style={{ marginBottom: '24px', width: '100%' }}>
                                <RadioGroup
                                    value={accountType}
                                    onChange={(e) => setAccountType(e.target.value)}
                                    style={{ 
                                        flexDirection: 'row', 
                                        justifyContent: 'center',
                                        gap: '32px'
                                    }}
                                >
                                    <FormControlLabel 
                                        value="individual" 
                                        control={<Radio sx={{ 
                                            color: '#002F87',
                                            '&.Mui-checked': {
                                                color: '#002F87',
                                            }
                                        }} />} 
                                        label={<Typography style={{ fontSize: '1rem', fontWeight: '500' }}>Individual</Typography>}
                                    />
                                    <FormControlLabel 
                                        value="business" 
                                        control={<Radio sx={{ 
                                            color: '#002F87',
                                            '&.Mui-checked': {
                                                color: '#002F87',
                                            }
                                        }} />} 
                                        label={<Typography style={{ fontSize: '1rem', fontWeight: '500' }}>Business</Typography>}
                                    />
                                </RadioGroup>
                            </FormControl>

                            {/* Business Information */}
                            {accountType === 'business' && (
                                <>
                                    <Typography variant="h6" style={{ 
                                        marginBottom: '16px', 
                                        color: '#002F87',
                                        fontWeight: 'bold'
                                    }}>
                                        Business Information
                                    </Typography>
                                    <TextField
                                        required
                                        fullWidth
                                        label="Legal Business Name"
                                        value={businessInfo.legalName}
                                        onChange={(e) => {
                                            setBusinessInfo({ ...businessInfo, legalName: e.target.value });
                                            clearError('businessInfo.legalName');
                                        }}
                                        error={!!errors.businessInfo?.legalName}
                                        helperText={errors.businessInfo?.legalName ? 'This field is required' : ''}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Registration Number (optional)"
                                        value={businessInfo.registrationNumber}
                                        onChange={(e) =>
                                            setBusinessInfo({ ...businessInfo, registrationNumber: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="VAT Number (optional)"
                                        value={businessInfo.vatNumber}
                                        onChange={(e) => setBusinessInfo({ ...businessInfo, vatNumber: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="EORI / Import-Export Number (optional)"
                                        value={businessInfo.eoriNumber}
                                        onChange={(e) => setBusinessInfo({ ...businessInfo, eoriNumber: e.target.value })}
                                        sx={{ mb: 3 }}
                                    />
                                </>
                            )}

                            <SubmitButton 
                                fullWidth 
                                variant="contained" 
                                onClick={handleProceed}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Loading...' : 'Proceed'}
                            </SubmitButton>
                        </FormContainer>
                    </PaperContainer>
                </LoginContainer>
            </BackgroundContainer>
        </>
    );
}

export default ProceedToPreorderPage;
